import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { getBoundingClientRect } from 'libraries/front-end-core/helpers/document'
import cx from 'classnames'
import usePopover from 'hooks/usePopover'
import LinkIcon from '@mui/icons-material/Link'
import { Copy } from 'lucide-react'
import { toast } from 'react-toastify'
import notificationToast from 'helpers/notificationToast'

// Used for Glossary Terms
const Popover = () => {
  const { containerRef, click, hover, resize, getBoundingBox } = usePopover()

  return (
    <div className='popover-container relative h0 z10' ref={containerRef}>
      <div>
        {[...click, ...hover].map((item, idx) => (
          <PopoverItem {...{ ...item, resize, getBoundingBox }} key={idx} />
        ))}
      </div>
    </div>
  )
}

export default Popover

// Placeholder spliced into the raw HTML content; the real icon is portalled into it below.
const COPY_ICON_ANCHOR = '<span class="copy-icon-anchor"></span>'

const withCopyIconAnchor = (content) => {
  if (!content) {
    return content
  }

  return /<\/\w+>\s*$/.test(content) && !/<\/a>\s*$/.test(content)
    ? content.replace(/(<\/\w+>)\s*$/, `${COPY_ICON_ANCHOR}$1`)
    : `${content}${COPY_ICON_ANCHOR}`
}

const PopoverItem = ({ getBoundingBox, ...props }) => {
  const [glossaryPosition, setGlossaryPosition] = useState({ bottom: 0, left: 0, width: 0 })
  const [arrow, setArrow] = useState(0)
  const [left, setLeft] = useState(0)
  const [show, setShow] = useState(false)
  const popoverRef = useRef(null)
  const contentRef = useRef(null)
  const boundingBox = getBoundingBox()
  const [iconAnchor, setIconAnchor] = useState(null)

  useEffect(() => {
    setIconAnchor(contentRef.current?.querySelector('.copy-icon-anchor') ?? null)
  }, [props.content])

  const handleCopy = (evt) => {
    if (evt.target.closest('a') || window.getSelection().toString()) {
      return
    }

    navigator.clipboard.writeText(contentRef.current?.textContent ?? '')
    notificationToast(<LinkIcon />, toast.TYPE.INFO, 'Definition copied to clipboard')
  }

  useEffect(() => {
    const { bottom, left, width } = getBoundingClientRect(props.el)
    setGlossaryPosition({ bottom, left, width })
  }, [props.el, props.resize])

  useEffect(() => {
    const { width } = getBoundingClientRect(popoverRef.current)
    const left = Math.max(
      0,
      Math.min(
        glossaryPosition.left + glossaryPosition.width / 2 - width / 2 - boundingBox.left,
        boundingBox.width - width
      )
    )
    const arrow = glossaryPosition.left + glossaryPosition.width / 2 - boundingBox.left - left
    setArrow(arrow)
    setLeft(left)
    setShow(true)
  }, [glossaryPosition])

  const style = {
    top: glossaryPosition.bottom - boundingBox.top,
    left,
    borderStyle: 'hidden'
  }

  const className = cx(
    'br2 pa0 popover bottom z10 absolute top-100 db o-0 default-cursor',
    { show }
  )

  return (
    <div {...{ className, style }} ref={popoverRef}>
      <div
        className='arrow'
        style={{ left: arrow }}
      />
      <div
        ref={contentRef}
        className='f6 fw4 br2 bg-teal white popover-content hover-reveal pa4 pointer'
        onClick={handleCopy}
        dangerouslySetInnerHTML={{ __html: withCopyIconAnchor(props.content) }}
      />
      {iconAnchor && createPortal(
        <Copy className='hover-reveal-target transition-all o-0 valign-text-bottom ml3' size={16} />,
        iconAnchor
      )}
    </div>
  )
}
